import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatExpansionPanel } from '@angular/material/expansion';
import moment from 'moment';
import { combineLatest, fromEvent, Observable } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    startWith,
    take,
    takeWhile,
    tap
} from 'rxjs/operators';
import {
    IDirectReport,
    IExpenseItem,
    IPerDiemLocale,
    ITask,
    ITravelType
} from '../../../lookups';
import { Process } from '../../../shared';
import { Dialog, MenuItem } from '../../../shared/efaact-primeng.module';
import { LoggingService } from '../../../shared/services/logging/logging.service';
import { IUserColor, IUserInfo, UserRoleEnum } from '../../../user-info';
import { IExpenseLine } from '../../IExpenseLine';
import { IExpenseReceipt } from '../../IExpenseReceipt';
import { IExpenseSummary } from '../../IExpenseSummary';
import { ExpenseStatus, ExpenseRequestTypeEnum, TravelTypeEnum } from '../../expense-enums';

/** All intermediate values used to decide if detail/receipt grids may open edit (and PDBO link). */
interface ExpenseDetailCanEditComputation {
    canEdit: boolean;
    /** Final branch name for the decision. */
    reason: string;
    validating: boolean;
    approving: boolean;
    statusAllowsEdit: boolean;
    expenseReportStatus: number | null | undefined;
    hasDateRange: boolean;
    entryStartDateIso: string | null;
    entryEndDateIso: string | null;
    reportDateRaw: unknown;
    reportDateValid: boolean;
    reportDateIso: string | null;
    minDateIso: string | null;
    maxDateIso: string | null;
    /** `null` when entry window / report date checks were skipped. */
    inWindow: boolean | null;
}

/** Entry / super-user edit — blocked only in Validated or Approved. */
export function expenseReportStatusAllowsEntryEdit(
    expenseReportStatus: number | null | undefined
): boolean {
    if (expenseReportStatus == null) {
        return true;
    }
    return (
        expenseReportStatus !== ExpenseStatus.Validated &&
        expenseReportStatus !== ExpenseStatus.Approved
    );
}

/** Validation / approval read-only open — blocked only when Approved. */
export function expenseReportStatusAllowsReadOnlyOpen(
    expenseReportStatus: number | null | undefined
): boolean {
    if (expenseReportStatus == null) {
        return true;
    }
    return expenseReportStatus !== ExpenseStatus.Approved;
}

export function computeExpenseDetailCanEdit(params: {
    validating: boolean;
    approving: boolean;
    expenseReportStatus: number | null | undefined;
    reportDateRaw: unknown;
    entryStartDate: Date | undefined;
    entryEndDate: Date | undefined;
}): ExpenseDetailCanEditComputation {
    const {
        validating,
        approving,
        expenseReportStatus,
        reportDateRaw,
        entryStartDate,
        entryEndDate
    } = params;

    const entryStartDateIso =
        entryStartDate != null && moment(entryStartDate).isValid()
            ? moment(entryStartDate).toISOString()
            : null;
    const entryEndDateIso =
        entryEndDate != null && moment(entryEndDate).isValid()
            ? moment(entryEndDate).toISOString()
            : null;

    const statusAllowsEdit =
        expenseReportStatusAllowsEntryEdit(expenseReportStatus);

    if (validating || approving) {
        return {
            canEdit: false,
            reason: 'validationOrApprovalWorkflow',
            validating,
            approving,
            statusAllowsEdit,
            expenseReportStatus,
            hasDateRange: !!(entryStartDate && entryEndDate),
            entryStartDateIso,
            entryEndDateIso,
            reportDateRaw,
            reportDateValid: false,
            reportDateIso: null,
            minDateIso: null,
            maxDateIso: null,
            inWindow: null
        };
    }

    if (!statusAllowsEdit) {
        return {
            canEdit: false,
            reason: 'status',
            validating,
            approving,
            statusAllowsEdit,
            expenseReportStatus,
            hasDateRange: !!(entryStartDate && entryEndDate),
            entryStartDateIso,
            entryEndDateIso,
            reportDateRaw,
            reportDateValid: false,
            reportDateIso: null,
            minDateIso: null,
            maxDateIso: null,
            inWindow: null
        };
    }

    if (expenseReportStatus === ExpenseStatus.Disapproved) {
        return {
            canEdit: true,
            reason: 'disapproved',
            validating,
            approving,
            statusAllowsEdit,
            expenseReportStatus,
            hasDateRange: !!(entryStartDate && entryEndDate),
            entryStartDateIso,
            entryEndDateIso,
            reportDateRaw,
            reportDateValid: false,
            reportDateIso: null,
            minDateIso: null,
            maxDateIso: null,
            inWindow: null
        };
    }

    const hasDateRange = !!(entryStartDate && entryEndDate);

    if (!hasDateRange) {
        return {
            canEdit: true,
            reason: 'noStartEndDate',
            validating,
            approving,
            statusAllowsEdit,
            expenseReportStatus,
            hasDateRange,
            entryStartDateIso,
            entryEndDateIso,
            reportDateRaw,
            reportDateValid: false,
            reportDateIso: null,
            minDateIso: null,
            maxDateIso: null,
            inWindow: null
        };
    }

    const reportDate = moment(reportDateRaw);
    const reportDateValid = reportDate.isValid();
    const reportDateIso = reportDateValid ? reportDate.toISOString() : null;

    if (!reportDateValid) {
        return {
            canEdit: true,
            reason: 'invalidReportDate',
            validating,
            approving,
            statusAllowsEdit,
            expenseReportStatus,
            hasDateRange,
            entryStartDateIso,
            entryEndDateIso,
            reportDateRaw,
            reportDateValid,
            reportDateIso,
            minDateIso: null,
            maxDateIso: null,
            inWindow: null
        };
    }

    const minDate = moment(entryStartDate).startOf('day');
    const maxDate = moment(entryEndDate).endOf('day');
    const minDateIso = minDate.toISOString();
    const maxDateIso = maxDate.toISOString();
    const inWindow = reportDate.isBetween(minDate, maxDate, undefined, '[]');

    return {
        canEdit: inWindow,
        reason: 'dateWindow',
        validating,
        approving,
        statusAllowsEdit,
        expenseReportStatus,
        hasDateRange,
        entryStartDateIso,
        entryEndDateIso,
        reportDateRaw,
        reportDateValid,
        reportDateIso,
        minDateIso,
        maxDateIso,
        inWindow
    };
}

/** Whether the expense entry modal may open (includes read-only review in validation/approval). */
export function computeExpenseDetailCanOpen(params: {
    validating: boolean;
    approving: boolean;
    expenseReportStatus: number | null | undefined;
    reportDateRaw: unknown;
    entryStartDate: Date | undefined;
    entryEndDate: Date | undefined;
}): boolean {
    const result = computeExpenseDetailCanEdit(params);
    if (result.canEdit) {
        return true;
    }
    if (result.validating || result.approving) {
        const statusAllowsOpen = expenseReportStatusAllowsReadOnlyOpen(
            params.expenseReportStatus
        );
        if (!statusAllowsOpen) {
            return false;
        }
        if (params.expenseReportStatus === ExpenseStatus.Disapproved) {
            return true;
        }
        return result.inWindow !== false;
    }
    return false;
}

@Component({
    selector: 'ef-expense-list',
    templateUrl: './expense-list.component.html',
    styleUrls: ['./expense-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ExpenseListComponent
    implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
    @Input() expenses: Array<IExpenseSummary> = [];
    @Input() userWebColors: Array<IUserColor>;
    @Input() expenseLines: Array<IExpenseLine>;
    @Input() expenseReceipts: Array<IExpenseReceipt>;
    selectedExpenses: Array<number>;
    @Input() process: Process;
    @Input() loading: boolean;
    private _startDate: Date;
    @Input() public set startDate(value: Date) {
        const m = moment(value);
        if (m.isValid()) {
            this._startDate = m.toDate();
            this.filterFormGroup.controls['startDate'].patchValue(m.toDate());
        }
    }
    public get startDate(): Date {
        return this._startDate;
    }
    private _endDate: Date;
    @Input() public set endDate(value: Date) {
        const m = moment(value);
        if (m.isValid()) {
            this._endDate = m.toDate();
            this.filterFormGroup.controls['endDate'].patchValue(m.toDate());
        }
    }
    public get endDate(): Date {
        return this._endDate;
    }
    @Input() roleId: number;
    @Input() directReports: Array<IDirectReport>;
    @Input() perDiemTypes: Array<IPerDiemLocale>;
    @Input() travelTypes: Array<ITravelType>;
    @Input() tasks: Array<ITask>;
    @Input() expenseItems: Array<IExpenseItem>;
    @Input() selectedExpense: IExpenseSummary;
    @Input() selectedDetail: IExpenseLine;
    @Input() selectedUser: IDirectReport;
    @Input() superUserEnabled: boolean;
    @Input() userInfo: IUserInfo;
    @Input() validating: boolean;
    @Input() approving: boolean;
    @Input() showReimbursedAmount: boolean;
    @Input() failureMessage: string;
    @Input() successMessage: string;
    /** Entry window for detail/receipt editability (MinWorkDate − lag … MaxWorkDate). */
    private _entryStartDate: Date;
    @Input() public set entryStartDate(value: Date) {
        const m = value != null ? moment(value) : null;
        if (m?.isValid()) {
            this._entryStartDate = m.toDate();
        } else {
            this._entryStartDate = undefined;
        }
    }
    public get entryStartDate(): Date {
        return this._entryStartDate;
    }
    private _entryEndDate: Date;
    @Input() public set entryEndDate(value: Date) {
        const m = value != null ? moment(value) : null;
        if (m?.isValid()) {
            this._entryEndDate = m.toDate();
        } else {
            this._entryEndDate = undefined;
        }
    }
    public get entryEndDate(): Date {
        return this._entryEndDate;
    }

    /**
     * Single gate for detail grid / receipt grid edit actions (double-click, PDBO link),
     * computed from workflow flags, status, report date, and entry window.
     */
    public canEdit = false;
    /** True when the expense entry popup may open (includes read-only review). */
    public canOpen = false;

    @Output() addExpense = new EventEmitter<{
        summary: IExpenseSummary;
        travelTypeId: TravelTypeEnum;
    }>();
    /** Add a new detail line to the currently selected expense report. */
    @Output() addExpenseLine = new EventEmitter<{
        summary: IExpenseSummary;
        travelTypeId: TravelTypeEnum;
    }>();
    @Output() editExpense = new EventEmitter<IExpenseSummary>();
    @Output() ShowExpenseDetail = new EventEmitter<IExpenseSummary>();
    @Output() deleteExpense = new EventEmitter<IExpenseSummary>();
    @Output() oversignExpense = new EventEmitter<IExpenseSummary>();
    @Output() ConvertExpense = new EventEmitter<Array<IExpenseSummary>>();
    @Output() ValidateExpense = new EventEmitter<Array<IExpenseSummary>>();
    @Output() ApproveExpense = new EventEmitter<Array<IExpenseSummary>>();
    @Output() UnvalidateExpense = new EventEmitter<Array<IExpenseSummary>>();
    @Output() UnapproveExpense = new EventEmitter<Array<IExpenseSummary>>();
    @Output() DisapproveExpense = new EventEmitter<Array<IExpenseSummary>>();
    @Output() selectDetail = new EventEmitter<IExpenseLine>();
    @Output() editDetail = new EventEmitter<IExpenseLine>();
    @Output() deleteDetail = new EventEmitter<IExpenseLine>();
    @Output() deleteReceipt = new EventEmitter<IExpenseReceipt>();
    @Output() editReceipt = new EventEmitter<IExpenseReceipt>();
    @Output() selectReceipt = new EventEmitter<IExpenseReceipt>();
    @Output() dateFiltersChanged = new EventEmitter<{
        startDate: Date;
        endDate: Date;
    }>();
    @Output() CanClose = new EventEmitter<boolean>();
    // @Output() taskFilterChanged = new EventEmitter<Array<ITask>>();
    // @Output() expenseItemFilterChanged = new EventEmitter<Array<IExpenseItem>>();
    @Output() userSelected = new EventEmitter<IDirectReport>();
    @Output() AddSelectedExpense = new EventEmitter<number>();
    @Output() RemoveSelectedExpense = new EventEmitter<number>();
    @Output() ClearSelectedExpenses = new EventEmitter();
    @Output() ShowPdbo = new EventEmitter<IExpenseLine>();
    @ViewChild('summary', { static: true })
    matExpansionSummary: MatExpansionPanel;
    @ViewChild('validateExpense', { read: ElementRef, static: true })
    btnValidateExpenseEl: ElementRef;
    @ViewChild('approveExpense', { read: ElementRef, static: true })
    btnApproveExpenseEl: ElementRef;
    @ViewChild('unvalidateExpense', { read: ElementRef, static: true })
    btnUnvalidateExpenseEl: ElementRef;
    @ViewChild('unapproveExpense', { read: ElementRef, static: true })
    btnUnapproveExpenseEl: ElementRef;
    @ViewChild('disapproveExpense', { read: ElementRef, static: true })
    btnDisapproveExpenseEl: ElementRef;
    @ViewChild('disapprovalDialog', { static: true }) disapprovalDialog: Dialog;
    public RoleType = UserRoleEnum;
    public TravelTypeEnum = TravelTypeEnum;
    /** Summary toolbar: Add Expense Report dropdown (Per Diem / Mileage / Receipt). */
    public addExpenseReportMenuItems: MenuItem[] = [
        {
            label: 'Per Diem',
            icon: 'pi pi-plus',
            command: () =>
                this.onAddExpenseType(new Event('click'), TravelTypeEnum.PerDiem)
        },
        {
            label: 'Mileage',
            icon: 'pi pi-plus',
            command: () =>
                this.onAddExpenseType(new Event('click'), TravelTypeEnum.Mileage)
        },
        {
            label: 'Receipt',
            icon: 'pi pi-plus',
            command: () =>
                this.onAddExpenseType(new Event('click'), TravelTypeEnum.Receipt)
        }
    ];
    public directReportsGroup: UntypedFormGroup;
    public filterFormGroup: UntypedFormGroup;
    public showLineDetails = false;
    public panelOpenState = true;
    public dialogVisible = false;
    private componentActive = true;
    public validateClick$: Observable<any>;
    public approveClick$: Observable<any>;
    public unvalidateClick$: Observable<any>;
    public unapproveClick$: Observable<any>;
    public disapproveClick$: Observable<any>;
    public canOversign = false;
    private cdRef: ChangeDetectorRef;
    public get ExpenseLabel(): string {
        if (this.process && this.process.ProcessID === 147) {
            return 'Add Travel Request';
        } else {
            return 'Add Expense Report';
        }
    }

    /** Detail accordion: "Report Detail {ExpReportNbr}". */
    public get detailAccordionHeader(): string {
        const nbr = this.selectedExpense?.ExpReportNbr;
        return nbr != null && nbr > 0 ? `Report Detail ${nbr}` : 'Report Detail';
    }

    /** Receipt accordion: "Receipt {ExpReportNbr}". */
    public get receiptAccordionHeader(): string {
        const nbr = this.selectedExpense?.ExpReportNbr;
        return nbr != null && nbr > 0 ? `Receipt ${nbr}` : 'Receipt';
    }

    constructor(
        @Inject(LoggingService) private log: LoggingService,
        @Inject(Injector) injector: Injector
    ) {
        this.log.verbose({ type: 'ExpenseListComponent::' });
        this.cdRef = injector.get(ChangeDetectorRef);

        this.directReportsGroup = new UntypedFormGroup({
            superUser: new UntypedFormControl(null),
            ddlDirectReports: new UntypedFormControl(null),
            disapprovalComment: new UntypedFormControl(null)
        });

        this.filterFormGroup = new UntypedFormGroup({
            endDate: new UntypedFormControl(null),
            startDate: new UntypedFormControl(null)
            // tasksFilter: new FormControl(null),
            // expenseItemFilter: new FormControl(null),
        });
    }
    ngOnInit() {}
    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['validating'] ||
            changes['approving'] ||
            changes['selectedExpense'] ||
            changes['entryStartDate'] ||
            changes['entryEndDate']
        ) {
            this.updateCanEditExpenseDetailComponents();
        }
    }
    ngOnDestroy(): void {
        this.log.verbose({ type: 'ExpenseListComponent:ngOnDestroy' });
        this.componentActive = false;
    }
    ngAfterViewInit(): void {
        const mStartDate = moment(this.startDate);
        const mEndDate = moment(this.endDate);
        setTimeout(() => {
            // if (mStartDate.isValid()) {
            //     this.filterFormGroup.controls['startDate'].patchValue(
            //         mStartDate.toDate()
            //     );
            // }
            // if (mEndDate.isValid()) {
            //     this.filterFormGroup.controls['endDate'].patchValue(
            //         mEndDate.toDate()
            //     );
            // }
            this.directReportsGroup.controls['superUser'].patchValue(
                this.process.IsSuperUserProcess
            );
        }, 0);

        this.setupSubscriptions();
        this.log.verbose({
            type: 'ExpenseListComponent:ngAfterViewInit',
            process: this.process,
            approving: this.approving,
            validating: this.validating
        });
    }
    private updateCanEditExpenseDetailComponents(): void {
        const sourceInputs = {
            validating: this.validating,
            approving: this.approving,
            expenseReportStatus: this.selectedExpense?.ExpenseReportStatus,
            reportDateRaw: this.selectedExpense?.ReportDate,
            entryStartDate: this._entryStartDate,
            entryEndDate: this._entryEndDate
        };
        this.log.debug({
            type: 'ExpenseListComponent:computeExpenseDetailCanEdit:sources',
            expReportNbr: this.selectedExpense?.ExpReportNbr,
            processId: this.process?.ProcessID,
            sourceValidating: sourceInputs.validating,
            sourceApproving: sourceInputs.approving,
            sourceExpenseReportStatus: sourceInputs.expenseReportStatus,
            sourceReportDateRaw: sourceInputs.reportDateRaw,
            sourceReportDateMs:
                sourceInputs.reportDateRaw != null
                    ? moment(sourceInputs.reportDateRaw).valueOf()
                    : null,
            sourceReportDateIso: moment(sourceInputs.reportDateRaw).isValid()
                ? moment(sourceInputs.reportDateRaw).toISOString()
                : null,
            sourceEntryStartDate: this._entryStartDate,
            sourceEntryEndDate: this._entryEndDate,
            sourceEntryStartMs: this._entryStartDate?.getTime?.() ?? null,
            sourceEntryEndMs: this._entryEndDate?.getTime?.() ?? null,
            sourceEntryStartIso:
                this._entryStartDate != null &&
                moment(this._entryStartDate).isValid()
                    ? moment(this._entryStartDate).toISOString()
                    : null,
            sourceEntryEndIso:
                this._entryEndDate != null &&
                moment(this._entryEndDate).isValid()
                    ? moment(this._entryEndDate).toISOString()
                    : null
        });
        const result = computeExpenseDetailCanEdit(sourceInputs);
        this.canEdit = result.canEdit;
        this.canOpen = computeExpenseDetailCanOpen(sourceInputs);
        this.log.debug({
            type: 'ExpenseListComponent:computeExpenseDetailCanEdit:result',
            expReportNbr: this.selectedExpense?.ExpReportNbr,
            canEdit: result.canEdit,
            canOpen: this.canOpen,
            reason: result.reason,
            validating: result.validating,
            approving: result.approving,
            statusAllowsEdit: result.statusAllowsEdit,
            expenseReportStatus: result.expenseReportStatus,
            hasDateRange: result.hasDateRange,
            entryStartDateIso: result.entryStartDateIso,
            entryEndDateIso: result.entryEndDateIso,
            reportDateRaw: result.reportDateRaw,
            reportDateValid: result.reportDateValid,
            reportDateIso: result.reportDateIso,
            minDateIso: result.minDateIso,
            maxDateIso: result.maxDateIso,
            inWindow: result.inWindow
        });
        this.cdRef.markForCheck();
    }
    setupSubscriptions() {
        this.validateClick$ = fromEvent(
            this.btnValidateExpenseEl.nativeElement,
            'click'
        ).pipe(
            tap(x =>
                this.log.verbose({ type: 'ExpenseListComponent:validateClick' })
            ),
            tap(x => x.stopPropagation())
        );
        this.unvalidateClick$ = fromEvent(
            this.btnUnvalidateExpenseEl.nativeElement,
            'click'
        ).pipe(
            tap(x =>
                this.log.verbose({
                    type: 'ExpenseListComponent:unvalidateClick'
                })
            ),
            tap(x => x.stopPropagation())
        );
        this.approveClick$ = fromEvent(
            this.btnApproveExpenseEl.nativeElement,
            'click'
        ).pipe(
            tap(x =>
                this.log.verbose({ type: 'ExpenseListComponent:approveClick' })
            ),
            tap(x => x.stopPropagation())
        );
        this.unapproveClick$ = fromEvent(
            this.btnUnapproveExpenseEl.nativeElement,
            'click'
        ).pipe(
            tap(x =>
                this.log.verbose({
                    type: 'ExpenseListComponent:unapproveClick'
                })
            ),
            tap(x => x.stopPropagation())
        );
        this.disapproveClick$ = fromEvent(
            this.btnDisapproveExpenseEl.nativeElement,
            'click'
        ).pipe(
            tap(x =>
                this.log.verbose({
                    type: 'ExpenseListComponent:disapproveClick'
                })
            ),
            tap(x => x.stopPropagation())
        );
        this.directReportsGroup.controls['superUser'].valueChanges
            .pipe(
                takeWhile(() => this.componentActive),
                tap(x =>
                    this.log.debug({
                        type: 'ExpenseListComponent:directReportsGroup:superUser:valueChanges',
                        x
                    })
                ),
                filter((x: boolean) => x !== true)
            )
            .subscribe(x => {
                this.userSelected.emit(null);
            });

        this.directReportsGroup.controls['ddlDirectReports'].valueChanges
            .pipe(
                takeWhile(() => this.componentActive),
                startWith()
            )
            .subscribe((x: IDirectReport) => {
                this.log.debug({
                    type: 'ExpenseListComponent:directReportsGroup:ddlDirectReports:valueChanges',
                    x
                });
                this.userSelected.emit(x);
                this.clearDependentGrids();
            });

        combineLatest([
            this.filterFormGroup.controls['startDate'].valueChanges.pipe(
                startWith(this.startDate)
            ),
            this.filterFormGroup.controls['endDate'].valueChanges.pipe(
                startWith(this.endDate)
            )
        ])
            .pipe(
                debounceTime(500),
                distinctUntilChanged((a, b) => {
                    const [aStart, aEnd] = a || [];
                    const [bStart, bEnd] = b || [];
                    return (
                        moment(aStart).valueOf() === moment(bStart).valueOf() &&
                        moment(aEnd).valueOf() === moment(bEnd).valueOf()
                    );
                }),
                takeWhile(() => this.componentActive)
            )
            .subscribe(([startDate, endDate]) => {
                this.clearDependentGrids();
                this.dateFiltersChanged.emit({
                    startDate: startDate,
                    endDate: endDate
                });
            });
    }

    onAddExpenseType(event: Event, travelTypeId: TravelTypeEnum): void {
        event?.stopPropagation?.();
        let d = moment();
        const max = moment(this.endDate);
        const min = moment(this.startDate);
        d = moment.min(d, max);
        d = moment.max(d, min);
        const erTypeId =
            this.process?.ProcessID === 147 || this.process?.ProcessID === 152
                ? ExpenseRequestTypeEnum.Travel
                : ExpenseRequestTypeEnum.Expense;
        this.addExpense.emit({
            summary: {
                ExpReportNbr: -1,
                ReportDate: d.toDate(),
                ERTypeID: erTypeId
            },
            travelTypeId
        });
    }

    onAddExpenseTypeToSelected(
        event: Event,
        travelTypeId: TravelTypeEnum
    ): void {
        event?.stopPropagation?.();
        if (!this.selectedExpense?.ExpReportNbr || this.selectedExpense.ExpReportNbr <= 0) {
            this.log.warn({
                type: 'ExpenseListComponent:onAddExpenseTypeToSelected',
                message: 'No selected expense report to add a line to'
            });
            return;
        }
        this.addExpenseLine.emit({
            summary: { ...this.selectedExpense },
            travelTypeId
        });
    }

    private clearDependentGrids() {
        // Intentionally no-op for selection. Selected summary stays until the user
        // picks another Summary row, or getNewExpenses clears it on filter reload.
    }
    onClickExpense($event: IExpenseSummary) {
        this.log.debug({
            type: 'ExpenseListComponent:onClickExpense',
            $event
        });
        this.ShowExpenseDetail.emit($event);
    }
    onSelectExpenses($event: IExpenseSummary[]) {
        this.log.debug({
            type: 'ExpenseListComponent:onSelectExpenses',
            $event
        });
        this.selectedExpenses = $event.map(x => x.ExpReportNbr);
    }
    onEditExpense($event: IExpenseSummary) {
        this.editExpense.emit($event);
    }
    onDeleteExpense($event: IExpenseSummary) {
        this.deleteExpense.emit($event);
    }
    onDeleteDetail($event: IExpenseLine) {
        this.deleteDetail.emit($event);
    }
    onDeleteReceipt($event: IExpenseReceipt) {
        this.log.verbose({
            type: 'ExpenseListComponent:onDeleteReceipt',
            $event
        });
        this.deleteReceipt.emit($event);
    }
    onOversignExpense($event: IExpenseSummary) {
        this.oversignExpense.emit($event);
    }
    onSelectDetail($event: IExpenseLine) {
        this.selectDetail.emit($event);
    }
    onSelectReceipt($event: IExpenseReceipt) {
        this.selectReceipt.emit($event);
    }
    onEditDetail($event: IExpenseLine) {
        this.editDetail.emit($event);
    }
    onEditReceipt($event: IExpenseReceipt) {
        this.editReceipt.emit($event);
    }
    onConvertExpense($event: Array<IExpenseSummary>) {
        this.ConvertExpense.emit($event);
    }
    onValidateExpense($event: Array<IExpenseSummary>) {
        this.ValidateExpense.emit($event);
    }
    onApproveExpense($event: Array<IExpenseSummary>) {
        this.ApproveExpense.emit($event);
    }
    onUnvalidateExpense($event: Array<IExpenseSummary>) {
        this.UnvalidateExpense.emit($event);
    }
    onUnapproveExpense($event: Array<IExpenseSummary>) {
        this.UnapproveExpense.emit($event);
    }
    onDisapproveExpense($event: Array<IExpenseSummary>) {
        this.log.debug({
            type: 'ExpenseListComponent:onDisapproveExpense',
            $event
        });
        this.disapprovalDialog.onHide.pipe(take(1)).subscribe(() => {
            this.onCloseComment($event);
        });
        this.dialogVisible = true;
        this.disapprovalDialog.visible = true;
    }
    onCloseComment(expenses: Array<IExpenseSummary>) {
        this.log.debug({
            type: 'ExpenseListComponent:onCloseComment',
            expenses
        });
        const comment =
            this.directReportsGroup.get('disapprovalComment')?.value;
        this.DisapproveExpense.emit(
            expenses.map(x => ({ ...x, MgrComment: comment }))
        );
    }
    onCanClose($event: boolean) {
        this.CanClose.next($event);
    }
    onAddSelectedExpense($event) {
        this.AddSelectedExpense.emit($event);
    }
    onRemoveSelectedExpense($event) {
        this.RemoveSelectedExpense.emit($event);
    }
    onClearSelectedExpenses($event) {
        this.ClearSelectedExpenses.emit($event);
    }
    onShowPdbo($event: IExpenseLine) {
        this.log.debug({
            type: 'ExpenseListComponent:onShowPdbo',
            $event
        });
        this.ShowPdbo.emit($event);
    }
}
